import { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import EditableFullRuneTree from "@ugg/shared/components/common/RuneTrees/EditableFullRuneTree";
import { RuneTreeCondensed } from "./RuneTreeCondensed";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";
import { TableSortLabel } from "@ugg/shared/components/common/TableSortLabel";
import { numberWithCommas, round } from "@ugg/shared/utils/math";
import { AdvBuildInfo } from "@ugg/shared/api/data-parser/champions/adv-builds";
import { ReactComponent as ChevronUp2 } from "@ugg/shared/assets/svg/chevron-up-2.svg";

interface RuneSetListProps {
  sets: AdvBuildInfo["keystone_pairs"][number]["runes"];
  viewAll: boolean;
  sort: ["pick" | "win", boolean];
  setSort: (sortBy: "pick" | "win") => void;
  patch: string;
  totalMatches: number;
}

export function RuneSetList(props: RuneSetListProps) {
  const { t } = useTranslation();
  const { sets, viewAll, sort, setSort, patch, totalMatches } = props;
  const [sortBy, sortDir] = sort;

  return (
    <div className="rounded-[3px] overflow-hidden">
      <div className="flex items-center justify-between p-[18px] bg-purple-200">
        <HeaderBullet>
          {t(`Runes`)}
          {viewAll && (
            <span className="ml-[12px] font-medium text-lavender-50">{"(Sets with pick rates <1% will not be shown)"}</span>
          )}
        </HeaderBullet>
        <div className="flex items-center gap-[24px] w-[170px]">
          <div className="w-[46%] whitespace-nowrap cursor-pointer" onClick={() => setSort("pick")}>
            <TableSortLabel
              className={classNames("flex items-center justify-center text-[14px]", {
                "text-white font-semibold": sortBy === "pick",
                "text-lavender-50 font-medium": sortBy !== "pick",
              })}
              active={sortBy === "pick"}
              direction={sortDir ? "desc" : "asc"}
              labelOffset={5}
            >
              {t(`Pick Rate`)}
            </TableSortLabel>
          </div>
          <div className="w-[38%] whitespace-nowrap cursor-pointer" onClick={() => setSort("win")}>
            <TableSortLabel
              className={classNames("flex items-center justify-center text-[14px]", {
                "text-white font-semibold": sortBy === "win",
                "text-lavender-50 font-medium": sortBy !== "win",
              })}
              active={sortBy === "win"}
              direction={sortDir ? "desc" : "asc"}
              labelOffset={5}
            >
              {t(`Win Rate`)}
            </TableSortLabel>
          </div>
        </div>
      </div>
      <div>
        {sets.map((runes, index) => {
          return (
            <RuneRow
              key={`${runes.keystone_id}-${runes.sub_style}-${index}`}
              patch={patch}
              data={runes}
              totalMatches={totalMatches}
            />
          );
        })}
      </div>
    </div>
  );
}

interface RuneRowProps {
  data: AdvBuildInfo["keystone_pairs"][number]["runes"][number];
  patch: string;
  totalMatches: number;
}

const RuneRow = (props: RuneRowProps) => {
  const [expanded, setExpanded] = useState(false);
  const { data, patch, totalMatches } = props;

  return (
    <div
      className={classNames("border-t-[1px] border-purple-500 bg-purple-200 cursor-pointer hover:bg-purple-400", {
        "bg-purple-400": expanded,
      })}
      onClick={() => {
        setExpanded(!expanded);
      }}
    >
      <div className="flex items-center h-[60px] px-[18px]">
        <div
          className={classNames("flex items-center justify-center mr-[8px] w-[20px] h-[20px] rounded-[3px]", {
            "bg-purple-400": !expanded,
            "bg-purple-500": expanded,
          })}
        >
          <ChevronUp2
            className={classNames("w-[8px] [&>path]:fill-white", {
              "rotate-180": !expanded,
            })}
          />
        </div>
        <div className="pr-[18px] border-r-[1px] border-accent-purple-700">
          <RuneTreeCondensed perkStyle={data.primary_style} activePerks={data.active_perks} patch={patch} ssr pathLabel />
        </div>
        <div className="pl-[18px]">
          <RuneTreeCondensed perkStyle={data.sub_style} activePerks={data.active_perks} patch={patch} ssr pathLabel />
        </div>
        <div className="ml-[24px] flex-1 flex justify-end">
          <div className="flex items-center gap-[24px] w-[170px]">
            <div className="w-[46%] text-center">
              <div className="text-white text-[14px] font-bold">{`${round((data.matches / totalMatches) * 100, 2)}%`}</div>
              <div className="text-[12px] font-medium text-lavender-50">{numberWithCommas(data.matches)}</div>
            </div>
            <div className="w-[38%] text-[14px] font-medium text-lavender-50 text-center">{`${data.win_rate}%`}</div>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="[&_.rune-trees-container]:justify-center [&_.rune-trees-container]:gap-[44px] p-[18px]">
          <EditableFullRuneTree patch={patch} recRunes={data} />
        </div>
      )}
    </div>
  );
};
